import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, MapPin } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CARE_SITES } from "@/data/careSites";
import HeritageMark from "@/components/common/HeritageMark";

const EMAIL = "info@heracq.com";

const applyMailto = (regionLabel) => {
  const subject = encodeURIComponent(`Caregiver Application — ${regionLabel}`);
  const body = encodeURIComponent(
    `Hi Heritage team,\n\nI'd like to apply to join your caregiver bench in ${regionLabel}.\n\nName:\nPhone:\nCertifications / License:\nAvailability (hours & days):\nYears of experience:\nShort note about your care experience:\n\nThank you!`,
  );
  return `mailto:${EMAIL}?subject=${subject}&body=${body}`;
};

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.0,
      delay: 0.1 + i * 0.12,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

function RegionPanel({ site }) {
  const h = site.hiringRates;
  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pt-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-5"
      >
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.26em] text-[#C86A51]">
          <MapPin size={14} /> {site.region}
        </div>
        <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tight mt-5 leading-[1.03] text-[#FDFBF7]">
          {site.brand}
        </h3>
        <p className="mt-6 text-base md:text-lg text-[#FDFBF7]/80 leading-relaxed max-w-md">
          {h.note}
        </p>

        <a
          href={applyMailto(site.region)}
          data-testid={`careers-apply-${site.key}`}
          className="mt-10 inline-flex items-center gap-2 bg-[#C86A51] hover:bg-[#B35A42] text-white rounded-full px-7 py-3.5 text-sm font-medium transition-colors group"
        >
          Apply to {site.region}
          <ArrowUpRight
            size={16}
            className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </a>

        <ul className="mt-10 space-y-3 text-[#FDFBF7]/90">
          {h.perks.map((p) => (
            <li key={p} className="flex items-start gap-3">
              <CheckCircle2 size={16} className="mt-1 text-[#C86A51]" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-7"
      >
        <div className="rounded-2xl border border-[#FDFBF7]/15 bg-[#1A2621]/60 overflow-hidden backdrop-blur-sm">
          <div className="hidden md:grid grid-cols-12 px-7 py-4 bg-[#FDFBF7]/5 text-[11px] uppercase tracking-[0.22em] text-[#FDFBF7]/60">
            <div className="col-span-5">Role</div>
            <div className="col-span-4">Pay range</div>
            <div className="col-span-3 text-right">Notes</div>
          </div>
          <ul className="divide-y divide-[#FDFBF7]/10">
            {h.roles.map((r, i) => (
              <li
                key={r.role}
                data-testid={`careers-${site.key}-role-${i}`}
                className="px-6 md:px-7 py-6 md:py-7 grid md:grid-cols-12 gap-3 md:gap-6 items-center hover:bg-[#FDFBF7]/5 transition-colors"
              >
                <div className="md:col-span-5 font-serif-display text-xl md:text-2xl text-[#FDFBF7]">
                  {r.role}
                </div>
                <div className="md:col-span-4">
                  <div className="font-serif-display text-xl md:text-2xl text-[#C86A51] leading-none">
                    {r.pay}
                  </div>
                </div>
                <div className="md:col-span-3 md:text-right text-sm text-[#FDFBF7]/70">
                  {r.notes}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-6 text-sm text-[#FDFBF7]/60 max-w-2xl">
          Pay bands are starting points based on experience and
          certification. All roles are W-2 employment with workers’
          compensation and liability coverage included.
        </p>
      </motion.div>
    </div>
  );
}

export default function CareersPage() {
  const [region, setRegion] = useState("nj");
  const nj = CARE_SITES.nj;
  const ny = CARE_SITES.ny;

  useEffect(() => {
    const prev = document.title;
    document.title = "Careers · Heritage Homecare Holdings Group";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <main
      data-testid="careers-page"
      className="min-h-screen bg-[#1A2621] text-[#FDFBF7] font-sans-body overflow-hidden"
    >
      {/* Top brand bar */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-10 flex items-center justify-between">
        <Link to="/" data-testid="careers-logo" className="flex items-center gap-3 group">
          <HeritageMark size={40} bg="#FDFBF7" stroke="#1A2621" accent="#C86A51" />
          <div className="font-serif-display leading-none">
            <div className="text-xl md:text-2xl">Heritage</div>
            <div className="text-[11px] uppercase tracking-[0.26em] text-[#FDFBF7]/60 mt-1">
              Careers
            </div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-6 text-sm">
          <Link
            to="/nj"
            className="text-[#FDFBF7]/80 hover:text-[#C86A51] transition-colors"
          >
            NJ Site
          </Link>
          <Link
            to="/ny"
            className="text-[#FDFBF7]/80 hover:text-[#C86A51] transition-colors"
          >
            NY Site
          </Link>
          <a
            href={`mailto:${EMAIL}`}
            className="text-[#FDFBF7]/80 hover:text-[#C86A51] transition-colors inline-flex items-center gap-1"
          >
            {EMAIL}
            <ArrowUpRight size={12} />
          </a>
        </div>
      </div>

      {/* Spotlight */}
      <div className="pointer-events-none absolute left-0 right-0 top-0 h-[600px] bg-gradient-to-b from-[#C86A51]/8 via-transparent to-transparent" />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="eyebrow text-[#C86A51]"
        >
          Join the Heritage team
        </motion.div>

        <h1
          data-testid="careers-headline"
          className="font-serif-display mt-6 text-[clamp(2.5rem,7vw,5.75rem)] leading-[0.98] tracking-tight max-w-4xl"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">
              Great caregiving
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span variants={line} initial="hidden" animate="show" custom={1} className="block italic text-[#E6EAE3]/90">
              deserves great pay.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-[#FDFBF7]/80 leading-relaxed"
        >
          We’re hiring companions, home health aides, nurses, and live-in
          caregivers across New Jersey and New York. Choose your region
          below to see roles, pay bands, and apply.
        </motion.p>
      </section>

      {/* Region tabs */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <Tabs value={region} onValueChange={setRegion}>
          <TabsList
            data-testid="careers-region-tabs"
            className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 rounded-full p-1 h-auto"
          >
            <TabsTrigger
              value="nj"
              data-testid="careers-tab-nj"
              className="data-[state=active]:bg-[#C86A51] data-[state=active]:text-white text-[#FDFBF7]/70 rounded-full px-6 py-2 text-sm"
            >
              New Jersey
            </TabsTrigger>
            <TabsTrigger
              value="ny"
              data-testid="careers-tab-ny"
              className="data-[state=active]:bg-[#C86A51] data-[state=active]:text-white text-[#FDFBF7]/70 rounded-full px-6 py-2 text-sm"
            >
              New York
            </TabsTrigger>
          </TabsList>

          <TabsContent value="nj" className="mt-0">
            <RegionPanel site={nj} />
          </TabsContent>
          <TabsContent value="ny" className="mt-0">
            <RegionPanel site={ny} />
          </TabsContent>
        </Tabs>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-[#FDFBF7]/10 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[#FDFBF7]/60">
          <div>
            © {new Date().getFullYear()} Heritage Homecare Holdings Group LLC.
            All rights reserved.
          </div>
          <div className="font-serif-display italic text-[#FDFBF7]/80">
            Compassion · Dignity · Reliability
          </div>
        </div>
      </footer>
    </main>
  );
}
